import React, { useEffect, useRef, useState } from 'react'
import { Grid, InputLabel, Spinner, Tooltip, useTheme, useTranslation } from '@paintscout/ui'
import { DropdownSelect, useDialogs } from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import { useAddProcessingPaymentMutation } from '@paintscout/api'
import type { PayrixTxnMetadata } from 'paintscout'
import { getPayrixCredentials, randomString } from '@paintscout/util/util'
import { formatName, fromPayrixErrorCode, getPaymentFailureText } from '@paintscout/util/builder'
import { useSnackbar } from 'notistack'
import { createLogger } from '@paintscout/logger'
import { usePayrixStyles } from './usePayrixStyles'
import { abortIfPaymentBlocked } from './abortIfPaymentBlocked'

const log = createLogger('ach-form')
import type { FormProps } from './CollectPayrixPaymentDialog'

export default function ACHForm(props: FormProps) {
  const classes = usePayrixStyles()
  const { __ } = useTranslation()
  // Synchronous guard against a double-fire of submit. React's `submitting` state only disables the
  // button after a re-render, so a fast second click can still fire handleSubmit and create a second
  // PayFields charge before that. A ref flips immediately. (Does not stop a transport-level retry of
  // the hosted submit — that is caught server-side by the webhook duplicate-charge detection.)
  const submittingRef = useRef(false)
  const [addProcessingPaymentMutation] = useAddProcessingPaymentMutation()
  const [accountType, setAccountType] = useState<DropdownSelectOption>({ label: __('Personal Checking'), value: 8 })
  const theme = useTheme()
  const {
    amount,
    checkPaymentBlocked,
    companyId,
    handleSuccessfulPayment,
    handlePayFieldsFailure,
    isDeposit,
    merchantId,
    note,
    options,
    payFieldsLoading,
    paymentRequestId,
    quote,
    setActivityId,
    setPayFieldsLoading,
    setSubmitting,
    source,
    surcharge
  } = props
  const { enqueueSnackbar } = useSnackbar()
  const { dismissAllDialogs } = useDialogs()
  const isCanadianPayrix = options.options.integrations?.payrix?.config?.country === 'Canada'

  useEffect(() => {
    // Load the Payrix script
    let submitButton
    let handleSubmit
    let activityId
    const { publicKey, apiUrl } = getPayrixCredentials(options)

    const script = document.createElement('script')
    script.src = `${apiUrl}/payFieldsScript?spa=1`
    script.type = 'text/javascript'
    script.async = true
    script.onload = () => {
      const PayFields = ((window as any).PayFields as any) || {}
      PayFields.config.apiKey = publicKey
      PayFields.config.merchant = merchantId
      PayFields.config.mode = 'txn'
      PayFields.config.txnType = 'ecsale'
      PayFields.config.amount = Math.round(amount * 100)
      PayFields.customizations.optionalFields = ['#payment_cvv', '#expiration']
      PayFields.customizations.placeholders = {
        '#account_number': '123456789',
        '#routing': isCanadianPayrix ? '010156789' : '12345678',
        '#name': ''
      }
      PayFields.config.order = quote._id

      PayFields.fields = [
        // Number must be present for PayField divs to load
        {
          type: 'number',
          element: '#number'
        },
        {
          type: 'name',
          element: '#name',
          values: {
            name: quote.contact ? formatName(quote.contact) || null : null
          }
        },
        {
          type: 'account_number',
          element: '#account_number'
        },
        {
          type: 'account_type',
          element: '#account_type'
        },
        {
          type: 'routing',
          element: '#routing'
        }
      ]
      PayFields.customizations.style = {
        '.number': {
          display: 'none'
        },
        '.input': {
          ...theme.typography.body1,
          border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
          borderRadius: theme.borderRadius.md,
          boxSizing: 'border-box',
          height: '40px',
          padding: '10px 16px',
          width: '100%',
          fontSize: '1rem',
          color: theme.palette.text.primary
        },
        '.card-icon': {
          marginRight: '6rem',
          marginTop: '.25rem'
        }
      }

      PayFields.ready()

      // Set setPayFieldsLoading false after 1 second to prevent flickering
      setTimeout(() => {
        setPayFieldsLoading(false)
      }, 1000)

      handleSubmit = async () => {
        if (submittingRef.current) return
        submittingRef.current = true
        setSubmitting(true)
        setPayFieldsLoading(true)
        PayFields.customizations.optionalFields = ['#number']

        // Last fresh-state sample before money moves — account entry takes minutes, exactly the
        // window where a delayed paymentError write from a prior timed-out attempt lands.
        if (
          await abortIfPaymentBlocked({
            checkPaymentBlocked,
            submittingRef,
            setSubmitting,
            setPayFieldsLoading,
            enqueueSnackbar,
            translate: __,
            closeDialogs: dismissAllDialogs
          })
        ) {
          return
        }

        // create an optimistic activityId and create the activity on success
        activityId = randomString({ length: 32, charset: 'abcdefghijklmnopqrstuvwxyz0123456789' })

        // Tracks the client-side start of the payment, so an orphaned Payrix webhook (the browser
        // closed before onSuccess/addProcessingPayment ran) can be correlated back to this activityId.
        log.info('ACH payment submit pressed', {
          sentry: true,
          activityId,
          quoteId: quote._id,
          companyId
        })

        // 90/100 characters already used
        const payrixMeta: PayrixTxnMetadata = {
          aId: activityId,
          cId: companyId,
          t: quote.is_invoice ? 'p' : 'd',
          env: process.env.DEPLOYMENT_STAGE
        }

        PayFields.config.additionalData = { txnDescription: JSON.stringify(payrixMeta) }

        PayFields.submit()
      }

      submitButton = document.querySelector('#payrix-submit-button')
      submitButton?.addEventListener('click', handleSubmit)

      // Failure Callback
      PayFields.onFailure = function (err) {
        submittingRef.current = false
        setSubmitting(false)
        setPayFieldsLoading(false)
        // A decline carries the txn Payrix created; the server records it from there (#24621).
        void handlePayFieldsFailure(activityId, err?.data?.[0]?.id)
        const errors = (err.errors as any[]) || []
        // Top-level fields reach Sentry as extra data; `data` alone does not. Ties this failure to the
        // activityId the webhooks will look for (#24267).
        const failureContext = { activityId, quoteId: quote._id, companyId, payrixErrors: errors }
        const error15 = errors.find((error) => error.code === 15)
        if (error15) {
          if (error15.field === 'payment.number') {
            log.info('PayFields ACH invalid account number', { data: { error: err } })
            enqueueSnackbar(__('Please enter a valid account number'), { variant: 'error' })
          } else if (error15.errorCode === 'txn_limit_exceeded') {
            log.info('PayFields ACH transaction limit exceeded', { data: { error: err } })
            enqueueSnackbar(__('Transaction limit exceeded. Please contact support.'), { variant: 'error' })
          } else if (error15.msg) {
            log.warn('PayFields ACH code 15 with msg', { data: { error: err }, ...failureContext, sentry: true })
            // msg is bank wording; show the customer-safe line, as Stripe does
            enqueueSnackbar(
              __(getPaymentFailureText(fromPayrixErrorCode(error15.errorCode, error15.msg), 'customer')),
              {
                variant: 'error'
              }
            )
          } else {
            log.warn('PayFields ACH code 15 validation error', {
              data: { error: err },
              ...failureContext,
              sentry: true
            })
            enqueueSnackbar(__('Validation error. Please make sure all fields are complete and correct.'), {
              variant: 'error'
            })
          }
        } else {
          log.error('PayFields ACH unexpected failure', { data: { error: err }, ...failureContext, sentry: true })
          enqueueSnackbar(__('Something went wrong, please try again later.'), { variant: 'error' })
        }
      }

      // Success Callback
      PayFields.onSuccess = async function (response) {
        setActivityId(activityId)

        // Tracks that PayFields succeeded client-side, before addProcessingPayment creates the
        // activity server-side — the window where a closed browser can orphan the payment.
        log.info('ACH PayFields onSuccess', {
          sentry: true,
          activityId,
          quoteId: quote._id,
          companyId
        })

        // ACH always uses txn mode — PayFields returns the txnId directly
        const txnId = response?.data?.[0]?.id as string | undefined

        // addProcessingPayment records the payment AND creates the payment activity on the invoice
        // (server-side). When the #18623 flag is off, the resolver falls back to creating just the
        // activity (legacy behavior), so this single call is correct in both modes.
        await addProcessingPaymentMutation({
          variables: {
            quoteId: quote._id,
            activityId,
            amount,
            type: 'ACH',
            note: note?.length > 100 ? `${note.substring(0, 100)}...` : note,
            deposit: !!isDeposit,
            txnId,
            source,
            surcharge,
            ...(paymentRequestId ? { paymentRequestId } : {})
          }
        })

        handleSuccessfulPayment()
      }

      PayFields.onValidationFailure = function () {
        console.log('onValidationFailure')
        submittingRef.current = false
        setSubmitting(false)
        setPayFieldsLoading(false)
      }
    }

    document.head.appendChild(script)

    return () => {
      // cleanup
      const PayFields = ((window as any).PayFields as any) || {}
      submitButton.removeEventListener('click', handleSubmit)
      document.head.removeChild(script)
      PayFields.unmountAll()
      ;(window as any).PayFields = undefined
    }
  }, [])

  return (
    <>
      {payFieldsLoading && <Spinner style={{ display: 'block', margin: '81px auto', width: 30 }} />}
      <Grid container item spacing={2} className={classes.root} style={{ display: payFieldsLoading ? 'none' : 'flex' }}>
        <Grid item xs={12} md={6} className={classes.payField}>
          <InputLabel className={classes.inputLabel} htmlFor="name">
            {__('Full Name')}
          </InputLabel>
          <div id="name"></div>
        </Grid>
        <Grid item xs={12} md={6} className={classes.payField}>
          <InputLabel className={classes.inputLabel} htmlFor="account_number">
            {__('Account Number')}
          </InputLabel>
          <div id="account_number"></div>
        </Grid>
        <Grid item xs={12} md={6}>
          <DropdownSelect
            fullWidth
            label={__('Account Type')}
            options={[
              { label: __('Personal Checking'), value: 8 },
              { label: __('Personal Savings'), value: 9 },
              { label: __('Corporate (Business) Checking'), value: 10 },
              { label: __('Corporate (Business) Savings'), value: 11 }
            ]}
            variant="single"
            value={accountType}
            onChange={setAccountType}
            popperModifiers={{
              flip: {
                enabled: false
              }
            }}
          />
          <input id="account_type" type="hidden" value={accountType.value} />
        </Grid>
        <Grid item xs={12} md={6} className={classes.payField}>
          <Tooltip
            classes={{ badge: classes.inputTooltipBadge }}
            content={
              isCanadianPayrix
                ? __(
                    'Your routing number starts with a 0, then your 3-digit institution number, and ends with your 5-digit transit number (e.g., institution 101 and transit 56789 ➞ 010156789)'
                  )
                : undefined
            }
            disabled={!isCanadianPayrix}
            hideIcon={!isCanadianPayrix}
          >
            <InputLabel className={classes.inputLabel} htmlFor="routing">
              {__('Routing Number')}
            </InputLabel>
          </Tooltip>
          <div id="routing"></div>
        </Grid>
        <div style={{ display: 'none' }}>
          <div id="number"></div> {/* Number must be present for PayField divs to load */}
        </div>
      </Grid>
    </>
  )
}
